import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useSession } from '../auth/session';
import { Alerte as Encadre, Bouton, Champ, Invisible } from '../ui';
import { LIBELLE_MOTIF, MOTIFS, type MotifAlerte } from './urgence';

/**
 * Ce que le dépôt a produit. Deux formes, pas deux états d'une même : au-delà du quota
 * d'urgences, ce qui est écrit n'entre pas dans la chaîne des alertes mais dans celle des
 * messages, et ne se relit donc pas au même endroit.
 */
type Depot =
  | { outcome: 'alerte'; alerte: { downgraded: boolean } }
  | { outcome: 'message' };

/**
 * L'entrée Urgence du menu, et la fenêtre de dépôt.
 *
 * Elle vivait hors des onglets, en bas de la navigation : sur téléphone, une barre rouge
 * pleine largeur sous la barre des catégories, deux rangées de commandes empilées sur les
 * quelques centimètres qui restaient au contenu. Elle est désormais **dans le menu**, avec
 * les autres gestes qui ne sont pas des écrans — une seule porte en haut à droite au lieu
 * de deux barres en bas.
 *
 * Elle reste rouge, et elle reste la première du menu. C'est le seul endroit du produit où
 * la couleur rouge dit quelque chose plutôt que de décorer, et un geste qu'on pose dans
 * l'urgence ne se cherche pas en bas d'une liste.
 *
 * La fenêtre dit trois choses avant le champ de saisie, et dans cet ordre — parce que
 * c'est l'ordre dans lequel elles comptent une fois qu'on a cliqué :
 *
 * 1. **Ce que ça fait** : ça sonne sur le téléphone de l'autre parent même en silencieux,
 *    et ça revient toutes les minutes jusqu'à son accusé.
 * 2. **Ce que ça laisse** : une trace horodatée et inaltérable, lisible dans l'export.
 *    Ce n'est pas une menace, c'est la contrepartie du privilège de faire sonner.
 * 3. **La limite** : deux urgences par vingt-quatre heures. Au-delà, ce qui est écrit part
 *    quand même — en message, ou en alerte simple si la messagerie n'est pas ouverte. Le
 *    dire ici évite de le découvrir en croyant avoir alerté.
 */
export function EntreeUrgence({ surChoix }: { surChoix: () => void }) {
  return (
    <button
      type="button"
      role="menuitem"
      className="menu-compte-entree urgence"
      onClick={surChoix}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
        <path
          fill="currentColor"
          d="M12 2.6c.63 0 1.21.33 1.53.87l9.1 15.4c.66 1.12-.15 2.53-1.45 2.53H2.82c-1.3 0-2.11-1.41-1.45-2.53l9.1-15.4c.32-.54.9-.87 1.53-.87Zm0 5.4a1.2 1.2 0 0 0-1.2 1.26l.3 5a.9.9 0 0 0 1.8 0l.3-5A1.2 1.2 0 0 0 12 8Zm0 8.4a1.3 1.3 0 1 0 0 2.6 1.3 1.3 0 0 0 0-2.6Z"
        />
      </svg>
      Urgence
    </button>
  );
}

/**
 * L'entrée et la fenêtre sont deux composants et non un seul, et ce n'est pas un
 * découpage de confort : le menu se referme au moment où l'on choisit « Urgence ». Tant
 * que la fenêtre vivait dans l'entrée, la fermeture du menu la démontait — le dépôt
 * disparaissait dans le geste même qui l'ouvrait. Le menu garde donc l'état, l'entrée ne
 * fait que le signaler, et la fenêtre se rend en dehors du panneau.
 */
export function FenetreDepot({
  caseFileId,
  onFermer,
}: {
  caseFileId: string;
  onFermer: () => void;
}) {
  const dialogue = useRef<HTMLDialogElement>(null);
  const { client } = useSession();
  const queryClient = useQueryClient();
  const [motif, setMotif] = useState<MotifAlerte | null>(null);
  const [texte, setTexte] = useState('');
  // « Urgent » n'est pas présélectionné : le niveau qui réveille quelqu'un se choisit,
  // il ne se subit pas par défaut de lecture.
  const [niveau, setNiveau] = useState<'urgent' | 'normal' | null>(null);
  // Décochée : faire sonner à plein volume le téléphone de quelqu'un est une escalade, et
  // une escalade se coche. Elle n'a de sens qu'avec « tout de suite » — l'option
  // disparaît autrement plutôt que de rester grisée à côté d'un choix qu'elle ne
  // concerne pas.
  const [sonner, setSonner] = useState(false);
  /**
   * Deux étapes, et l'ordre n'est pas neutre : **ce qui se passe** avant **comment
   * prévenir**.
   *
   * Tout sur un écran, le choix du niveau arrivait sous la ligne de flottaison, et
   * l'option sonore — qui ne se montre qu'avec « tout de suite » — n'existait à l'écran
   * qu'après un geste qu'on n'avait aucune raison de faire à cet endroit. Elle était donc
   * invisible pour qui ne descendait pas.
   *
   * Séparer les deux répare autre chose de plus important : on décrit les faits d'abord,
   * on décide de l'escalade ensuite. Poser les deux questions ensemble invite à cocher
   * « tout de suite » pendant qu'on est encore sous le coup de l'événement.
   */
  const [etape, setEtape] = useState<1 | 2>(1);
  const titreEtape = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const element = dialogue.current;
    if (!element || element.open) return;
    element.showModal?.();
  }, []);

  // Le titre de l'étape prend le focus au changement : sans cela, le lecteur d'écran
  // reste sur le bouton « Suivant » qui vient de disparaître, et rien n'annonce que
  // l'écran a changé.
  useEffect(() => {
    if (etape === 2) titreEtape.current?.focus();
  }, [etape]);

  /**
   * Ce que le serveur a réellement écrit — et ce n'est pas toujours une alerte.
   *
   * Deux urgentes par dossier et par 24 h glissantes ; au-delà, le dépôt part **en
   * message**, dans le fil « Urgences » (docs/02 §4 bis). Quand la messagerie n'est pas
   * ouverte — dossier à un seul parent, ou compte sans abonnement — il retombe sur
   * l'alerte simple, parce qu'une alerte ne peut jamais être refusée.
   *
   * Ce qui manquait, et qui manque toujours, c'est de le **dire**. Sans cet écran, un
   * parent croit avoir alerté, ne voit rien arriver chez l'autre — un message comme une
   * alerte simple respectent les heures silencieuses — et en conclut que le produit est
   * cassé.
   */
  const [issue, setIssue] = useState<'message' | 'degradee' | null>(null);

  const deposer = useMutation({
    mutationFn: () =>
      client.request<Depot>(`/case-files/${caseFileId}/alerts`, {
        method: 'POST',
        body: {
          level: niveau,
          reason: motif,
          body: texte.trim(),
          sound: niveau === 'urgent' && sonner,
        },
      }),
    onSuccess: (depot) => {
      void queryClient.invalidateQueries({ queryKey: ['alertes', caseFileId] });
      if (depot.outcome === 'message') {
        // Le dépôt n'a rien écrit dans les alertes : c'est la messagerie qu'il faut
        // rafraîchir, sinon le fil « Urgences » reste vide jusqu'au prochain passage.
        void queryClient.invalidateQueries({ queryKey: ['messages', caseFileId] });
        setIssue('message');
        return;
      }
      if (depot.alerte.downgraded) {
        // On garde l'écran : fermer ici ferait disparaître l'information au moment précis
        // où elle compte.
        setIssue('degradee');
        return;
      }
      dialogue.current?.close();
    },
  });

  const etape1Complete = motif !== null && texte.trim().length > 0;
  const complet = etape1Complete && niveau !== null;

  return (
    <dialog
      className="depot-urgence"
      ref={dialogue}
      aria-labelledby="urgence-titre"
      onClose={onFermer}
    >
      {issue !== null ? (
        <div className="depot-urgence-contenu depot-urgence-degradee">
          <h2 id="urgence-titre">
            {issue === 'message' ? 'Envoyée en message' : 'Envoyée en alerte simple'}
          </h2>
          <p>
            Deux alertes urgentes ont déjà été déposées dans ce dossier au cours des
            dernières 24 heures.{' '}
            {issue === 'message'
              ? "Au-delà, ce que vous écrivez part en message — c'est ce qui empêche l'urgence de perdre son sens à force d'être employée."
              : "Au-delà, elles partent au niveau simple — c'est ce qui empêche l'urgence de perdre son sens à force d'être employée."}
          </p>
          <p>
            <strong>Votre message est bien enregistré</strong>
            {issue === 'message' ? (
              <>
                , dans le fil «&nbsp;Urgences&nbsp;» de la messagerie. Il y est horodaté et
                inaltérable, exactement comme une alerte.
              </>
            ) : (
              <>, et l'autre parent le verra dans le dossier.</>
            )}
          </p>
          {/* L'information qui manquait vraiment : sans elle, on attend une notification
              qui ne viendra qu'au matin, et on croit à une panne. */}
          <p className="discret">
            En revanche, {issue === 'message' ? 'un message' : 'une alerte simple'} respecte
            les heures silencieuses de l'autre parent — 21h–8h par défaut. Si elles sont en
            cours, {issue === 'message' ? 'il' : 'elle'} lui sera remis{issue === 'message' ? '' : 'e'}{' '}
            le matin.
          </p>
          <div className="depot-urgence-actions">
            <Bouton onClick={() => dialogue.current?.close()}>J'ai compris</Bouton>
          </div>
        </div>
      ) : (
      <form
        className="depot-urgence-contenu"
        onSubmit={(evenement) => {
          evenement.preventDefault();
          if (etape === 1) {
            if (etape1Complete) setEtape(2);
            return;
          }
          if (complet) deposer.mutate();
        }}
      >
        <h2 id="urgence-titre">Alerter l’autre parent</h2>
        <p className="depot-urgence-etape discret">Étape {etape} sur 2</p>

        {etape === 1 ? (
          <>
            <fieldset className="depot-urgence-motifs">
              <legend>Ce qui se passe</legend>
              {MOTIFS.map((entree) => (
                <label
                  key={entree.valeur}
                  className={motif === entree.valeur ? 'actif' : undefined}
                >
                  <input
                    type="radio"
                    name="motif"
                    value={entree.valeur}
                    checked={motif === entree.valeur}
                    onChange={() => setMotif(entree.valeur)}
                  />
                  <span className="depot-urgence-libelle">{entree.libelle}</span>
                  <span className="discret">{entree.exemple}</span>
                </label>
              ))}
            </fieldset>

            <Champ
              label="En une phrase"
              value={texte}
              onChange={(evenement) => setTexte(evenement.target.value)}
              aide="Ce que l’autre parent doit savoir tout de suite. Le détail viendra après."
              maxLength={280}
            />

            {/* L'action qui fait avancer est toujours à droite (`.depot-urgence-actions`),
                comme sur les écrans de création : un parent qui vient de vivre l'urgence
                ne doit pas la chercher à un endroit différent d'un écran à l'autre. */}
            <div className="depot-urgence-actions">
              <div className="depot-urgence-actions-secondaires">
                <Bouton variante="secondaire" onClick={() => dialogue.current?.close()}>
                  Annuler
                </Bouton>
              </div>
              <Bouton type="submit" disabled={!etape1Complete}>
                Suivant
              </Bouton>
            </div>
          </>
        ) : (
          <>
            {/* `tabIndex={-1}` : cible de focus au changement d'étape, sans entrer dans
                l'ordre de tabulation. */}
            <h3 className="depot-urgence-sous-titre" ref={titreEtape} tabIndex={-1}>
              Comment le prévenir
            </h3>

            <p className="depot-urgence-rappel discret">
              {LIBELLE_MOTIF[motif as MotifAlerte]} — «&nbsp;{texte.trim()}&nbsp;»
            </p>

            <fieldset className="depot-urgence-niveaux">
              <legend>
                <Invisible>Comment prévenir l’autre parent</Invisible>
              </legend>
              <label className={niveau === 'urgent' ? 'actif' : undefined}>
                <input
                  type="radio"
                  name="niveau"
                  checked={niveau === 'urgent'}
                  onChange={() => setNiveau('urgent')}
                />
                <span className="depot-urgence-libelle">Tout de suite</span>
                <span className="discret">
                  Sonne même en silencieux et la nuit, et revient toutes les minutes
                  jusqu’à son accusé de réception — pendant une demi-heure au plus.
                </span>
              </label>
              <label className={niveau === 'normal' ? 'actif' : undefined}>
                <input
                  type="radio"
                  name="niveau"
                  checked={niveau === 'normal'}
                  onChange={() => {
                    setNiveau('normal');
                    setSonner(false);
                  }}
                />
                <span className="depot-urgence-libelle">Dès qu’il regarde</span>
                <span className="discret">
                  Notifié comme le reste, en respectant ses heures silencieuses.
                </span>
              </label>
            </fieldset>

            {niveau === 'urgent' ? (
              <label className={`depot-urgence-son ${sonner ? 'actif' : ''}`}>
                <input type="checkbox" checked={sonner} onChange={() => setSonner(!sonner)} />
                <span className="depot-urgence-libelle">Alerte sonore</span>
                <span className="discret">
                  Sonne à plein volume à l’ouverture de l’application. L’autre parent peut
                  couper ce son depuis ses réglages, et l’alerte lui parviendra quand même.
                </span>
              </label>
            ) : null}

            <p className="depot-urgence-trace discret">
              Une alerte est horodatée et inaltérable : l’avoir déposée, et à quelle heure,
              se lit dans l’export du dossier. Deux urgences par 24 h ; au-delà, ce que vous
              écrivez part quand même, mais sans sonner.
            </p>

            {deposer.isError ? (
              <Encadre titre="L’alerte n’est pas partie">
                Réessayez dans un instant. Si cela ne fonctionne toujours pas et que c’est
                grave, appelez.
              </Encadre>
            ) : null}

            <div className="depot-urgence-actions">
              <div className="depot-urgence-actions-secondaires">
                <Bouton variante="secondaire" onClick={() => setEtape(1)}>
                  Retour
                </Bouton>
                <Bouton variante="secondaire" onClick={() => dialogue.current?.close()}>
                  Annuler
                </Bouton>
              </div>
              <Bouton type="submit" disabled={!complet || deposer.isPending}>
                {deposer.isPending ? 'Envoi…' : 'Alerter'}
              </Bouton>
            </div>
          </>
        )}
      </form>
      )}
    </dialog>
  );
}
